import { useEffect, useMemo, useState } from "react";
import { Check, Pencil } from "lucide-react";
import moment from "moment";

import { RichEditor } from "components/RichEditor/RichEditor";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { InsertPickerModal } from "components/ui/InsertPickerModal/InsertPickerModal";
import { Select } from "components/ui/Select/Select";
import { stripHtml } from "utils/stripHtml";

import { AddDocumentsModal } from "./components/AddDocumentsModal/AddDocumentsModal";
import { ChooseEmailTemplateModal } from "./components/ChooseEmailTemplateModal/ChooseEmailTemplateModal";
import { useInsertCertificate } from "./hooks/useInsertCertificate";
import { useInsertLetter } from "./hooks/useInsertLetter";
import { useInsertNote } from "./hooks/useInsertNote";
import { useInsertPrescription } from "./hooks/useInsertPrescription";
import { useSendEmailForm } from "./hooks/useSendEmailForm";
import s from "./SendEmailModal.module.css";
import { parseNoteDate } from "./utils/formatContent";

export const SendEmailModal = ({
  isOpen,
  onClose,
  patient,
  documentIds = "",
  documents = [],
  inline = false,
  consultationId,
  refetchEmails,
}) => {
  const [userSelectedDocs, setUserSelectedDocs] = useState([]);
  const [showAddDocuments, setShowAddDocuments] = useState(false);

  const mergedDocuments = useMemo(() => {
    const map = new Map();
    documents.forEach((doc) => {
      if (doc?.id != null) map.set(String(doc.id), doc);
    });
    userSelectedDocs.forEach((doc) => {
      if (doc?.id != null) map.set(String(doc.id), doc);
    });

    return Array.from(map.values());
  }, [documents, userSelectedDocs]);

  const mergedDocumentIds = useMemo(() => {
    const ids = new Set();
    if (documentIds) {
      documentIds
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
        .forEach((id) => ids.add(id));
    }
    mergedDocuments.forEach((doc) => {
      if (doc?.id != null) ids.add(String(doc.id));
    });

    return Array.from(ids).join(",");
  }, [documentIds, mergedDocuments]);

  useEffect(() => {
    if (!isOpen) {
      setUserSelectedDocs([]);
      setShowAddDocuments(false);
    }
  }, [isOpen]);

  const {
    subject,
    setSubject,
    body,
    setBody,
    editorKey,
    senderEmail,
    setSenderEmail,
    senderEmails,
    isLoadingSenderEmails,
    senderEmailOptions,
    appendToBody,
    handleTemplateSelect,
    handleSend,
    handleClose,
    reset,
    isSending,
    recipientEmail,
    setRecipientEmail,
    isEditingEmail,
    setIsEditingEmail,
    isValidEmail,
  } = useSendEmailForm({
    patient,
    documentIds: mergedDocumentIds,
    consultationId,
    onClose,
    refetchEmails,
  });

  const noteInsert = useInsertNote({
    patientId: patient?.id,
    onInsert: appendToBody,
  });
  const prescriptionInsert = useInsertPrescription({
    patientId: patient?.id,
    onInsert: appendToBody,
  });
  const letterInsert = useInsertLetter({
    patientId: patient?.id,
    onInsert: appendToBody,
  });
  const certificateInsert = useInsertCertificate({
    patientId: patient?.id,
    onInsert: appendToBody,
  });

  const formContent = (
    <div>
      <div className={s.patientCard}>
        <div className={s.patientRow}>
          <span className={s.patientLabel}>Patient: </span>
          {patient?.firstName} {patient?.surname}
        </div>
        <div className={s.patientRow}>
          <span className={s.patientLabel}>Email: </span>
          {isEditingEmail ? (
            <span className={s.emailEditRow}>
              <input
                type="email"
                className={`${s.input} ${s.emailInput}`}
                value={recipientEmail}
                placeholder="Enter recipient email"
                onChange={(e) => setRecipientEmail(e.target.value)}
                autoFocus
              />
              <button
                type="button"
                aria-label="Save email"
                className={s.iconButton}
                onClick={() => setIsEditingEmail(false)}
              >
                <Check size={16} />
              </button>
            </span>
          ) : (
            <span className={s.emailEditRow}>
              <span
                className={
                  !isValidEmail(recipientEmail) ? s.emailErrorText : ""
                }
              >
                {recipientEmail || "No email address"}
              </span>
              <button
                type="button"
                aria-label="Edit recipient email"
                className={s.iconButton}
                onClick={() => setIsEditingEmail(true)}
              >
                <Pencil size={14} />
              </button>
            </span>
          )}
        </div>
        {!isValidEmail(recipientEmail) && !isEditingEmail && (
          <span className={s.errorText}>
            Please enter a valid email address before sending.
          </span>
        )}
      </div>

      <div className={s.fromSubjectGrid}>
        <div>
          <label htmlFor="emailSubject" className={s.fieldLabel}>
            Subject *
          </label>
          <input
            id="emailSubject"
            type="text"
            className={s.input}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter email subject"
          />
        </div>
        <div>
          <label className={s.fieldLabel}>From (Sender Email)</label>
          <Select
            options={senderEmailOptions}
            value={senderEmail}
            onChange={setSenderEmail}
            disabled={
              isLoadingSenderEmails ||
              (senderEmails && senderEmails.length <= 1)
            }
            fullWidth
          />
        </div>
      </div>

      <div className={s.toolbar}>
        <ChooseEmailTemplateModal
          isOpen={inline || isOpen}
          onSelect={handleTemplateSelect}
          patientId={patient?.id}
          consultationId={consultationId}
        />
      </div>

      <div className={s.insertActions}>
        <Button variant="outline" size="small" onClick={noteInsert.openModal}>
          Insert Note
        </Button>
        <Button
          variant="outline"
          size="small"
          onClick={prescriptionInsert.openModal}
        >
          Insert Prescription
        </Button>
        <Button variant="outline" size="small" onClick={letterInsert.openModal}>
          Insert Letter
        </Button>
        <Button
          variant="outline"
          size="small"
          onClick={certificateInsert.openModal}
        >
          Insert Certificate
        </Button>
      </div>

      <div className={s.field}>
        <RichEditor
          key={editorKey}
          editorValue={body}
          onChange={(value) => setBody(value)}
        />
      </div>

      {mergedDocuments.length > 0 && (
        <div className={s.field}>
          <label className={s.fieldLabel}>
            Attachments ({mergedDocuments.length})
          </label>
          <div className={s.attachments}>
            {mergedDocuments.map((doc, index) => (
              <div
                key={doc.id || doc.key || index}
                className={s.attachmentItem}
              >
                <span className={s.attachmentIcon}>📎</span>
                <div>
                  <div className={s.attachmentName}>
                    {doc.name || "Untitled Document"}
                  </div>
                  <div className={s.attachmentType}>
                    {doc.documentType || "Unknown Type"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={s.actions}>
        {consultationId && (
          <Button variant="outline" size="large" onClick={reset}>
            New Email
          </Button>
        )}
        <Button
          variant="primary"
          size="large"
          onClick={handleSend}
          disabled={isSending || !isValidEmail(recipientEmail)}
        >
          {isSending ? "Sending..." : "Send Email"}
        </Button>
      </div>

      <InsertPickerModal
        isOpen={noteInsert.showModal}
        onClose={noteInsert.closeModal}
        title="Select Note to Insert"
        search={noteInsert.search}
        onSearchChange={noteInsert.setSearch}
        searchPlaceholder="Search notes..."
        isLoading={noteInsert.isLoading}
        loadingText="Loading notes..."
        emptyText="No notes found"
        items={noteInsert.filtered}
        onSelect={noteInsert.handleInsert}
        renderItem={(note) => (
          <>
            <div className={s.noteItemMeta}>
              {(() => {
                const parsed = parseNoteDate(note.startTime || note.created);
                return parsed.isValid()
                  ? parsed.format("DD-MM-YYYY HH:mm")
                  : "Date unknown";
              })()}
            </div>
            <div className={s.noteItemText}>
              {(() => {
                const plain = stripHtml(note.note);
                return (
                  plain.substring(0, 150) + (plain.length > 150 ? "..." : "")
                );
              })()}
            </div>
          </>
        )}
      />

      <AddDocumentsModal
        isOpen={showAddDocuments}
        onClose={() => setShowAddDocuments(false)}
        patientId={patient?.id}
        initiallySelectedIds={userSelectedDocs.map((d) => d.id)}
        onConfirm={(docs) => setUserSelectedDocs(docs)}
      />

      <InsertPickerModal
        isOpen={prescriptionInsert.showModal}
        onClose={prescriptionInsert.closeModal}
        title="Select Prescription to Insert"
        search={prescriptionInsert.search}
        onSearchChange={prescriptionInsert.setSearch}
        searchPlaceholder="Search prescriptions..."
        isLoading={prescriptionInsert.isLoading}
        loadingText="Loading prescriptions..."
        emptyText="No prescriptions found"
        items={prescriptionInsert.filtered}
        onSelect={prescriptionInsert.handleInsert}
        renderItem={(prescription) => (
          <>
            <div className={s.prescriptionItemMeta}>
              {moment(
                prescription.datePrescribed || prescription.created
              ).format("DD-MM-YYYY")}
              {" — "}
              {prescription.medications?.length || 0} medication(s)
            </div>
            <div className={s.prescriptionItemNote}>
              {prescription.note || "No note"}
            </div>
          </>
        )}
      />

      <InsertPickerModal
        isOpen={letterInsert.showModal}
        onClose={letterInsert.closeModal}
        title="Select Letter to Insert"
        search={letterInsert.search}
        onSearchChange={letterInsert.setSearch}
        searchPlaceholder="Search letters..."
        isLoading={letterInsert.isLoading}
        loadingText="Loading letters..."
        emptyText="No letters found"
        items={letterInsert.filtered}
        onSelect={letterInsert.handleInsert}
        renderItem={(letter) => (
          <>
            <div className={s.noteItemMeta}>
              {letter.documentCreated
                ? moment(letter.documentCreated).format("DD-MM-YYYY HH:mm")
                : "Date unknown"}
            </div>
            <div className={s.noteItemText}>
              {letter.name || "Untitled Letter"}
            </div>
          </>
        )}
      />

      <InsertPickerModal
        isOpen={certificateInsert.showModal}
        onClose={certificateInsert.closeModal}
        title="Select DEASP Certificate to Insert"
        search={certificateInsert.search}
        onSearchChange={certificateInsert.setSearch}
        searchPlaceholder="Search certificates..."
        isLoading={certificateInsert.isLoading}
        loadingText="Loading certificates..."
        emptyText="No certificates found"
        items={certificateInsert.filtered}
        onSelect={certificateInsert.handleInsert}
        renderItem={(certificate) => {
          const cert = certificate.deaspCertificate;

          return (
            <>
              <div className={s.noteItemMeta}>
                {cert?.certFormType === "MED1"
                  ? "Medical Certificate 1"
                  : cert?.certFormType === "MED2"
                  ? "Medical Certificate 2"
                  : cert?.certFormType ?? "—"}
              </div>
              <div className={s.noteItemText}>
                {cert?.natureOfCondition ||
                  cert?.natureOfConditionCode ||
                  "No condition"}
              </div>
            </>
          );
        }}
      />
    </div>
  );

  if (inline) return formContent;

  return (
    <Dialog open={isOpen} onClose={handleClose} title="Send Email" width={900}>
      {formContent}
    </Dialog>
  );
};
